import { createTheme } from "@mui/material/styles";
import { lightTheme } from "./theme";

const muiTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: lightTheme.colors.primary,
      dark: lightTheme.colors.primaryDark,
      light: lightTheme.colors.primary,
      contrastText: lightTheme.colors.white,
    },
    secondary: {
      main: lightTheme.colors.accent,
    },
    background: {
      default: lightTheme.colors.background,
      paper: lightTheme.colors.white,
    },
    text: {
      primary: lightTheme.colors.primaryText,
      secondary: lightTheme.colors.secondaryText,
    },
    error: {
      main: lightTheme.colors.error,
    },
    success: {
      main: lightTheme.colors.green,
      contrastText: lightTheme.colors.white,
    },
    warning: {
      main: lightTheme.colors.warning,
    },
    divider: lightTheme.colors.border,
    grey: {
      50: lightTheme.colors.tableHeader,
      100: lightTheme.colors.mobileNavBackground,
      500: lightTheme.colors.grey500,
      600: lightTheme.colors.grey600,
      700: lightTheme.colors.grey700,
    },
    // Semantic color palette - reusable across components
    colors: {
      blue: lightTheme.colors.blue,
      green: lightTheme.colors.green,
      red: lightTheme.colors.red,
      orange: lightTheme.colors.orange,
      teal: lightTheme.colors.teal,
      purple: lightTheme.colors.purple,
      pink: lightTheme.colors.pink,
      yellow: lightTheme.colors.yellow,
      avatarColors: lightTheme.colors.avatarColors,
    },
    // Navigation icon colors (matching calendar event colors)
    custom: {
      navCreate: lightTheme.colors.navCreate,
      navNoticeFeed: lightTheme.colors.navNoticeFeed,
      navManageCircles: lightTheme.colors.navManageCircles,
      navTeamManagement: lightTheme.colors.navTeamManagement,
    },
    // UI state colors
    ui: {
      backdrop: lightTheme.colors.backdrop,
      backdropStrong: lightTheme.colors.blackOverlay70,
      buttonHover: lightTheme.colors.buttonHover,
      white: lightTheme.colors.white,
    },
    // UI colors
    uiColors: {
      textDark: lightTheme.colors.textDark,
      textMedium: lightTheme.colors.textMedium,
    },
    // Event card colors
    event: {
      border: lightTheme.colors.eventBorder,
      overlay: lightTheme.colors.eventOverlay,
    },
    // Gradients
    gradients: {
      tealBlue: lightTheme.colors.gradientTealBlue,
      tealTransparent: lightTheme.colors.gradientTealTransparent,
      event: lightTheme.colors.gradientEvent,
      light: lightTheme.colors.gradientLight,
      heroSurface: lightTheme.colors.gradientHeroSurface,
    },
    // Shadows
    shadows: {
      light: lightTheme.colors.shadowLight,
      medium: lightTheme.colors.shadowMedium,
      dark: lightTheme.colors.shadowDark,
      teal: lightTheme.colors.shadowTeal,
      success: lightTheme.colors.shadowSuccess,
      warning: lightTheme.colors.shadowWarning,
      successFocus: lightTheme.colors.shadowSuccessFocus,
      inputFocusStrong: lightTheme.colors.inputFocusShadowStrong,
    },
    // Template preview colors
    template: {
      urgentStrong: lightTheme.colors.templateUrgentStrong,
      urgentLight: lightTheme.colors.templateUrgentLight,
      urgentPill: lightTheme.colors.templateUrgentPill,
      eventStrong: lightTheme.colors.templateEventStrong,
      eventLight: lightTheme.colors.templateEventLight,
      eventPill: lightTheme.colors.templateEventPill,
      infoStrong: lightTheme.colors.templateInfoStrong,
      infoLight: lightTheme.colors.templateInfoLight,
      infoPill: lightTheme.colors.templateInfoPill,
    },
    // Border colors
    borders: {
      slate: lightTheme.colors.borderSlate,
      slateHover: lightTheme.colors.borderSlateHover,
      slateLight: lightTheme.colors.borderSlateLight,
    },
    // Card colors
    cards: {
      blueBg: lightTheme.colors.cardBlueBg,
      blueHover: lightTheme.colors.cardBlueHover,
      blueText: lightTheme.colors.cardBlueText,
      pinkBg: lightTheme.colors.cardPinkBg,
      pinkHover: lightTheme.colors.cardPinkHover,
      pinkText: lightTheme.colors.cardPinkText,
      tealBg: lightTheme.colors.cardTealBg,
      tealHover: lightTheme.colors.cardTealHover,
      tealText: lightTheme.colors.cardTealText,
      purpleBg: lightTheme.colors.cardPurpleBg,
      purpleHover: lightTheme.colors.cardPurpleHover,
      purpleText: lightTheme.colors.cardPurpleText,
      orangeBg: lightTheme.colors.cardOrangeBg,
      orangeHover: lightTheme.colors.cardOrangeHover,
      templates: lightTheme.colors.templatesRose,
      birthdays: lightTheme.colors.birthdaysMint,
      certificates: lightTheme.colors.certificatesGold,
      signatures: lightTheme.colors.signaturesNavy,
    },
    // Hero gradient colors
    hero: {
      gradientStart: lightTheme.colors.heroGradientStart,
      gradientMid: lightTheme.colors.heroGradientMid,
      gradientEnd: lightTheme.colors.heroGradientEnd,
    },
    // Icon colors
    icons: {
      yellow: lightTheme.colors.iconYellow,
      yellowGlow: lightTheme.colors.iconYellowGlow,
      yellowGlowStrong: lightTheme.colors.iconYellowGlowStrong,
      green: lightTheme.colors.iconGreen,
      greenGlow: lightTheme.colors.iconGreenGlow,
      whiteGlow: lightTheme.colors.iconWhiteGlow,
    },
    // Text shadows
    textShadows: {
      dark: lightTheme.colors.textShadowDark,
      medium: lightTheme.colors.textShadowMedium,
    },
    // White overlays
    whiteOverlays: {
      overlay70: lightTheme.colors.whiteOverlay70,
      overlay65: lightTheme.colors.whiteOverlay65,
      overlay60: lightTheme.colors.whiteOverlay60,
      overlay45: lightTheme.colors.whiteOverlay45,
      overlay30: lightTheme.colors.whiteOverlay30,
      overlay25: lightTheme.colors.whiteOverlay25,
      overlay50: lightTheme.colors.whiteOverlay50,
      overlay90: lightTheme.colors.whiteOverlay90,
      overlay95: lightTheme.colors.whiteOverlay95,
    },
  },
  typography: {
    fontFamily:
      "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontSize: 16,
    fontWeightRegular: lightTheme.typography.fontWeight.normal,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: "2.5rem",
      fontWeight: lightTheme.typography.fontWeight.bold,
    },
    h2: {
      fontSize: "2rem",
      fontWeight: lightTheme.typography.fontWeight.bold,
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: lightTheme.typography.fontWeight.semibold,
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: lightTheme.typography.fontWeight.semibold,
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: lightTheme.typography.fontWeight.semibold,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: lightTheme.typography.fontWeight.semibold,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: lightTheme.typography.fontWeight.normal,
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: lightTheme.typography.fontWeight.normal,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
});

export default muiTheme;
